import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { IntlProvider, FormattedMessage } from 'react-intl';
import { isBrowser, isMobile, useBreakpoints } from '@ux/component-utilities';
import { golf, breakpoints } from './enums';
import eidBuilder from './eid';
import { TrayMenu, TrayTitle } from '@ux/tray-menu';
import Link from '@ux/link';
import Information from '@ux/icon/information';
import Phone from '@ux/icon/phone';

/**
 * Render and expose the LanguageSelector as a functional Component.
 *
 * @param {Object} props Properties
 * @api public
 */
export class ContactTray extends Component {
  constructor(props) {
    super(...arguments);

    this.eids = {
      contactUs: eidBuilder({
        key: golf.contactUs,
        manifest: props.manifest
      }),

      globalDirectory: eidBuilder({
        key: golf.globalDirectory,
        manifest: props.manifest
      }),

      help: eidBuilder({
        key: golf.help,
        manifest: props.manifest
      })
    };

    this.showSupportContacts = this.showSupportContacts.bind(this);
    this.state = {
      showPhoneNumbers: false
    };
  }

  render() {
    const props = this.props;

    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        { this.renderInternal() }
      </IntlProvider>
    );
  }

  /**
   * Render contact tray menu or link
   * @returns {React.ReactElement} tray menu or link
   */
  renderInternal() {
    const props = this.props;
    const supportDetails = props.supportContacts || {};
    const phoneNumber = supportDetails.technicalSupportPhone || '';
    const trayName = supportDetails.technicalSupportLabel;
    const telHref = `tel:${ phoneNumber.replace(/[^\d]/g, '') }`;

    if (!trayName || trayName === '') {
      return <div />;
    }

    const includesPhoneNumber = this.includesPhoneNumber(trayName, phoneNumber);

    if (props.privateLabelId === 1) {
      if (props.contactTrayEnabled) {
        return this.renderFullTray(
          supportDetails,
          trayName,
          includesPhoneNumber
        );
      }

      if (this.shouldShowCityContactsTray(supportDetails)) {
        return this.renderCityContactsTray(
          supportDetails,
          trayName,
          telHref,
          includesPhoneNumber
        );
      }
    }

    // If none of the trays are enabled, just show the tray name as a link
    // If {phone} make the number a telHref otherwise link to contact us
    const trayNameEid = includesPhoneNumber
      ? `uxp.hyd.utility_bar.support_phone.click`
      : this.eids.contactUs;

    let trayHref = props.urls.contactUs.href;
    if (includesPhoneNumber) {
      trayHref = telHref;
    } else if (props.privateLabelId !== 1) {
      trayHref = '#';
    }

    return (
      <div className='basic-phone-container'>
        <Link
          urlArgs={ props.urlArgs }
          href={ trayHref }
          className='btn basic-phone-btn'
          data-eid={ trayNameEid }>
          { this.renderTrayName(trayName, includesPhoneNumber) }
        </Link>
      </div>
    );
  }

  /**
   * Renders name to display on contact tray
   * @param  {String} trayName The translated text to display as the tray name
   * @param {Boolean} includesPhoneNumber whether or not the tray label includes the support number
   * @returns {React.ReactElement} div containing tray name
   * @private
   */
  renderTrayName(trayName, includesPhoneNumber) {
    return (
      <React.Fragment>
        { (isMobile(this.props.breakpoint) ||
          this.props.breakpoint === 'tablet' ||
          includesPhoneNumber) &&
          this.renderPhoneIcon()}
        <span className='basic-phone-text hidden-sm-down title-text'>
          { trayName }
        </span>
      </React.Fragment>
    );
  }

  /**
   * Check if tray name includes the phone number
   * @param {String} trayName contact tray label
   * @param {String} phoneNumber the technical support number
   * @returns {Boolean} true if tray name includes phone number
   * @private
   */
  includesPhoneNumber(trayName, phoneNumber) {
    return trayName.indexOf(phoneNumber) !== -1;
  }

  /**
   * Whether to show the city contacts tray
   *
   * Only shows the tray if there exists a city contact with a phone number that
   * does not match the market contact
   *
   * @param {Object} supportDetails Information from the support API
   * @returns {Boolean} true if we should show the city contacts tray, false otherwise
   * @private
   */
  shouldShowCityContactsTray(supportDetails) {
    if (!supportDetails || !supportDetails.cityContacts) return false;

    const cityContactKeys = Object.keys(supportDetails.cityContacts);
    for (let i = 0; i < cityContactKeys.length; i++) {
      const contact = supportDetails.cityContacts[cityContactKeys[i]];
      if (
        contact.technicalSupportPhone !== supportDetails.technicalSupportPhone
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Render the phone icon along with appropriate alternate text
   *
   * @returns {React.ReactElement} A `span` element with the phone icon and specified alt text
   * @private
   */
  renderPhoneIcon() {
    const props = this.props;

    return (
      <span title={ props.messages[golf.contactUs] } className='phone-header'>
        <Phone />
      </span>
    );
  }

  renderCityContacts(supportDetails) {
    const altContactsData = [];

    // Push alternative contacts
    if (
      supportDetails.cityContacts &&
      Object.keys(supportDetails.cityContacts).length
    ) {
      Object.keys(supportDetails.cityContacts).forEach(key => {
        altContactsData.push(supportDetails.cityContacts[key]);
      });
    }

    // If no city contacts were found, use market contact
    // Make sure to use technicalSupportDescription for the market contact
    if (!altContactsData.length) {
      altContactsData.push({
        technicalSupportPhone: supportDetails.technicalSupportPhone,
        technicalSupportHours: supportDetails.technicalSupportDescription
      });
    }

    const altContacts = altContactsData.map((contact, idx) => {
      const altPhone = contact.technicalSupportPhone || '';
      const hrefPhoneNumber = `tel:${ altPhone.replace(/[^\d]/g, '') }`;
      // If {phone} make the number a telHref otherwise link to contact us
      const trayNameEid = this.includesPhoneNumber
        ? `uxp.hyd.utility_bar.support_phone.click`
        : this.eids.contactUs;

      return (
        <li key={ idx }>
          <span>{contact.technicalSupportHours}</span>
          <a
            className='contact-link'
            data-eid={ trayNameEid }
            href={ hrefPhoneNumber }>
            {altPhone}
          </a>
        </li>
      );
    });

    return <ul className='alt-contact-list list-unstyled'>{altContacts}</ul>;
  }

  renderFederatedPartner() {
    const props = this.props;

    if (!props.federatedPartner) {
      return null;
    }

    const { phone, phoneDescription } = props.federatedPartner;
    const hrefPhoneNumber = `tel:${ phone.replace(/[^\d]/g, '') }`;

    return (
      <div className='federated-partner'>
        <div className='partner-description'>{ phoneDescription }</div>
        <a
          href={ hrefPhoneNumber }
          className='contact-link'
          data-eid='uxp.hyd.utility_bar.federated_phone.click'>
          { phone }
        </a>
      </div>
    );
  }

  renderCityContactsTray(supportDetails, trayName, includesPhoneNumber) {
    const trayLabel = this.renderTrayName(trayName, includesPhoneNumber);
    const { messages = {} } = this.props;

    return (
      <TrayMenu
        ariaLabel={ messages[golf.contactUs] }
        data-eid='uxp.hyd.utility_bar.city_contact_tray.tray.click'
        type='base'
        className='city-contacts-tray'
        name={ trayLabel }>
        <div className='container'>
          <div className='row'>
            <TrayTitle>
              <FormattedMessage id='Shared:UtilityBar:SupportPhone:Alternate:Numbers' />
            </TrayTitle>
            { this.renderCityContacts(supportDetails) }
            { this.renderFederatedPartner() }
            {this.renderSupportAnnouncement(
              supportDetails.technicalSupportAnnouncement
            )}
          </div>
        </div>
      </TrayMenu>
    );
  }

  renderFullTray(supportDetails, trayName, includesPhoneNumber) {
    const props = this.props;
    const eids = this.eids;
    const trayLabel = this.renderTrayName(trayName, includesPhoneNumber);

    return (
      <TrayMenu
        ariaLabel={ props.messages[golf.contactUs] }
        data-eid='uxp.hyd.utility_bar.contact_tray.tray.click'
        type='base'
        className='contact-tray'
        name={ trayLabel }>
        <div className='container'>
          { this.renderSupportAnnouncement(
            supportDetails.technicalSupportAnnouncementAllChannels,
            true,
            {
              label: supportDetails.technicalSupportAnnouncementBtnText,
              href: supportDetails.technicalSupportAnnouncementBtnHref
            }
          ) }
          <div className='row'>
            <div className='col-lg-4 col-md-6 content-wrap'>
              <TrayTitle>
                <FormattedMessage id='Shared:UtilityBar:SupportPhone:CallUs' />
              </TrayTitle>
              { this.renderCityContacts(supportDetails) }
              { this.renderFederatedPartner() }
              <div className='contact-link-info'>
                <FormattedMessage id='Shared:UtilityBar:SupportPhone:GlobalDirectory:GlobalDirectory' />
              </div>
              <Link
                urlArgs={ props.urlArgs }
                { ...props.urls.contactUs }
                data-eid={ eids.globalDirectory }
                className='contact-link'>
                <FormattedMessage id={ golf.globalDirectory } />
              </Link>
            </div>
            { this.renderRequestACall() }
            { isMobile(props.breakpoint) || props.breakpoint === 'tablet'
              ? this.renderMobileChatSection(supportDetails)
              : this.renderChatSection(supportDetails) }
            <div className='col-md-4 help-mobile hidden-md-up'>
              <TrayTitle>
                <FormattedMessage id='Shared:UtilityBar:SupportPhone:HelpCenter' />
              </TrayTitle>
              <p>
                <FormattedMessage id='Shared:UtilityBar:SupportPhone:HelpCenter:Explore' />
              </p>
              <div>
                <Link
                  className='ux-button ux-button-secondary btn btn-default'
                  urlArgs={ props.urlArgs }
                  { ...props.urls.help }
                  data-eid={ eids.help }>
                  <FormattedMessage id={ golf.help } />
                </Link>
              </div>
            </div>
          </div>
          {this.renderSupportAnnouncement(
            supportDetails.technicalSupportAnnouncement
          )}
        </div>
      </TrayMenu>
    );
  }

  showSupportContacts() {
    this.setState({
      showPhoneNumbers: true
    });
  }

  renderChatSection(supportDetails) {
    // Make chat data available in a global so chat team code can render it
    if (isBrowser()) {
      const uxel = window && window.ux && window.ux.eldorado;
      if (uxel) {
        const messages = this.props.messages;

        uxel.chat_data = {
          target: 'gdchat-container',
          utilityChatEnabled: this.props.utilityChatEnabled,

          messages: {
            ChatNow: messages['Shared:UtilityBar:SupportPhone:ChatNow'],
            Chat: messages['Shared:UtilityBar:SupportPhone:Chat'],
            ChatOffline: messages['Shared:UtilityBar:SupportPhone:ChatOffline'],
            ChatHours: supportDetails.liveChatHours
          }
        };
      }
    }

    // IMPORTANT: `#gdchat-container` is used by the Chat team to render their chat UI into.
    // Do not remove it or change it without talking to @clim & @mfloyd
    return (
      <div className='col-lg-4 col-md-6 content-wrap divider'>
        <div id='gdchat-container' />
      </div>
    );
  }

  renderMobileChatSection(supportDetails) {
    const showMobileChatSection =
      this.props.utilityChatEnabled &&
      supportDetails.liveChatHours &&
      supportDetails.contactTrayChatDescription &&
      supportDetails.contactTrayButtonLink;

    if (!showMobileChatSection) return null;

    return (
      <div className='col-md-4 chat-mobile hidden-md-up'>
        <TrayTitle>
          <FormattedMessage id='Shared:UtilityBar:SupportPhone:ChatNow' />
        </TrayTitle>
        <p>
          { supportDetails.contactTrayChatDescription }{ ' ' }
          <br />
          <span>{ supportDetails.liveChatHours }</span>
        </p>
        <div>
          { supportDetails.contactTrayButtonLink ? (
            <span className='mobile-chat-btn-wrap'>
              <Link
                className='ux-button ux-button-primary btn btn-info mobile-chat-btn'
                urlArgs={ this.props.urlArgs }
                data-eid='uxp.hyd.utility_bar.chat_support_btn.click'
                href={ supportDetails.contactTrayButtonLink }>
                <FormattedMessage id='Shared:UtilityBar:SupportPhone:ChatNow' />
              </Link>
            </span>
          ) : null }
        </div>
        { supportDetails.WhatsAppButtonLink &&
        supportDetails.WhatsAppPhoneNumber ? (
            <React.Fragment>
              <div className='whatsapp-container'>
                <span className='whatsapp-chat-btn'>
                  <Link
                    className='ux-button ux-button-secondary btn btn-default'
                    urlArgs={ this.props.urlArgs }
                    data-eid='uxp.hyd.utility_bar.whatsapp_chat_btn.click'
                    href={ supportDetails.WhatsAppButtonLink }>
                    <span aria-hidden='true' className='whatsapp-icon'>
                      <svg
                        role='img'
                        width='16'
                        height='16'
                        viewBox='24 10 24 24'
                        xmlns='http://www.w3.org/2000/svg'>
                        <path d='M44.504 13.488A11.877 11.877 0 0040.63 10.9 11.924 11.924 0 0036.05 10c-6.59 0-11.948 5.335-11.95 11.892a11.826 11.826 0 001.595 5.945L24 34l6.335-1.654a11.987 11.987 0 005.71 1.448h.005c6.586 0 11.948-5.336 11.95-11.893a11.772 11.772 0 00-.902-4.555 11.818 11.818 0 00-2.594-3.858zm-8.453 18.297h-.006a9.955 9.955 0 01-5.056-1.378l-.362-.214-3.76.981 1.004-3.65-.236-.373a9.83 9.83 0 01-1.517-5.257c0-5.45 4.458-9.884 9.937-9.884a9.958 9.958 0 017.022 2.9 9.862 9.862 0 012.906 6.99c-.002 5.45-4.458 9.885-9.932 9.885zm5.448-7.403c-.299-.149-1.767-.868-2.04-.97-.275-.1-.473-.147-.672.15-.2.297-.771.97-.946 1.165-.174.195-.348.223-.647.074-.298-.148-1.26-.462-2.401-1.475-.888-.787-1.487-1.76-1.662-2.059-.175-.297-.018-.456.132-.606.134-.133.298-.347.447-.52.149-.174.2-.298.298-.496.1-.198.05-.372-.024-.52-.075-.15-.672-1.612-.92-2.207-.243-.58-.49-.501-.672-.51-.172-.009-.374-.01-.574-.01a1.101 1.101 0 00-.796.371c-.273.298-1.044 1.017-1.044 2.48 0 1.462 1.07 2.875 1.218 3.074.15.198 2.106 3.2 5.1 4.486.556.239 1.124.448 1.702.627.716.229 1.367.194 1.88.118.574-.085 1.768-.719 2.017-1.413.248-.694.248-1.289.172-1.413-.077-.125-.27-.197-.568-.346z' />
                      </svg>
                    </span>
                    <FormattedMessage id='Sales:ContactTray:WhatsApp:ChatButton' />
                  </Link>
                </span>
                <p>{ supportDetails.WhatsAppPhoneNumber }</p>
              </div>
            </React.Fragment>
          ) : null }
      </div>
    );
  }

  renderRequestACall() {
    const props = this.props;

    if (!props.requestACallEnabled) {
      return null;
    }

    return (
      <div className='col-lg-4 col-md-6 content-wrap divider'>
        <div className='request-a-call'>
          <TrayTitle>
            <FormattedMessage id='Sales:ContactTray:RequestACall:CTA' />
          </TrayTitle>
          <p>
            <FormattedMessage id='Sales:ContactTray:RequestACall:avoidTheWait' />
          </p>
          <Link
            className='ux-button ux-button-secondary btn btn-default'
            urlArgs={ props.urlArgs }
            data-eid='uxp.hyd.utility_bar.request_a_call.click'
            { ...props.urls.requestACall }>
            <FormattedMessage id='Sales:ContactTray:RequestACall:CTA' />
          </Link>
        </div>
      </div>
    );
  }

  /**
   * Render the support announcement section if needed
   *
   * @param {Object} announcement value from supportDetails.technicalSupport{AllChannels}
   * @param {boolean} [allChannels] flag denoting whether we're rendering the `AllChannels` support announcement
   * @param {Object} [buttonInfo] settings for the button to show next to the AllChannels announcement if applicable
   * @returns {React.ReactElement} The support announcement as a React element, or null if none needed
   */
  renderSupportAnnouncement(
    announcement,
    allChannels = false,
    buttonInfo = {}
  ) {
    const className = allChannels
      ? 'support-announcement-all'
      : 'support-announcement';
    const announcementsTemplate = (
      <div className={ className }>
        <span className='support-announcement-text'>
          <Information />
          { announcement }
        </span>
        {buttonInfo.href ? (
          <span className='support-announcement-btn'>
            <Link
              className='ux-button ux-button-secondary ux-button-small btn btn-sm btn-default'
              urlArgs={ this.props.urlArgs }
              data-eid='uxp.hyd.utility_bar.support_announcement_btn.click'
              href={ buttonInfo.href }>
              { buttonInfo.label }
            </Link>
          </span>
        ) : null}
      </div>
    );

    if (announcement) {
      if (!allChannels) {
        return (
          <div className='row'>
            <div className='col-xs-12'>{ announcementsTemplate }</div>
          </div>
        );
      }
      return announcementsTemplate;
    }
    return null;
  }
}

ContactTray.propTypes = {
  supportContacts: PropTypes.object.isRequired,
  shopperId: PropTypes.string,
  contactTrayEnabled: PropTypes.bool,
  requestACallEnabled: PropTypes.bool,
  utilityChatEnabled: PropTypes.bool,
  manifest: PropTypes.string,
  market: PropTypes.string.isRequired,
  messages: PropTypes.object.isRequired,
  privateLabelId: PropTypes.number,
  urls: PropTypes.object,
  urlArgs: PropTypes.object,
  split: PropTypes.string,
  federatedPartner: PropTypes.object,
  breakpoint: PropTypes.string.isRequired
};

ContactTray.defaultProps = {
  contactTrayEnabled: false,
  requestACallEnabled: false,
  utilityChatEnabled: false
};

export default function ContactTrayWrapper(props) {
  const { breakpoint } = useBreakpoints(...breakpoints);
  return <ContactTray breakpoint={ breakpoint } { ...props }/>;
}
